import * as Rx from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { Collections } from '../collections.model';
import { arrayIsShallowEqual, toObservable } from './helpers';
import { ArrayElement, NearbyQueryParam, QueryParams } from './store.model';

/**
 * This file is the query class exposed to developers via datastore.list()
 *
 * It exposes a query that can be built up by chaining methods like:
 * ```lang=ts
 * this.datastore.list(
 *   'messages',
 *   this.auth.getUserId(),
 *   query => query.where('threadId', '==', this.thread.id).limit(50),
 * );
 * ```
 *
 * This API (and much of this class) is based on the interface from Firebase
 * and angularfire2, in particular:
 *   - firebase-js-sdk/packages/firestore/src/core/query.ts
 *   - firebase-js-sdk/packages/firestore/src/api/database.ts
 *   - firebase-js-sdk/packages/firestore-types/index.d.ts
 *   - angularfire2/src/firestore/interfaces.ts
 *   - angularfire2/src/firestore/firestore.ts
 */

/**
 * Filter conditions in a `Query.where()` clause are specified using the
 * strings '<', '<=', '==', '>=', and '>'.
 */
export type WhereFilterRangeOp = '<' | '<=' | '==' | '>=' | '>';
export type WhereFilterEqualsOp = '==';
export type WhereFilterStringComparisonOp = 'equalsIgnoreCase';
export type WhereFilterInListOp = 'in';
export type WhereFilterListIncludesOp = 'includes';
export type WhereFilterListIntersectsOp = 'intersects';
export type WhereNearbyFilterOp = 'nearby';

export type WhereFilterOp =
  | WhereFilterEqualsOp
  | WhereFilterRangeOp
  | WhereFilterStringComparisonOp
  | WhereFilterInListOp
  | WhereFilterListIncludesOp
  | WhereFilterListIntersectsOp
  | WhereNearbyFilterOp;

export type NumericOrStringField<K, T> = K extends keyof T
  ? T[K] extends number | string
    ? K
    : never
  : never;

export enum OrderByDirection {
  ASC,
  DESC,
}

export interface Ordering<K extends keyof Collections> {
  readonly field: NumericOrStringField<keyof Collections[K], Collections[K]>;
  readonly direction: OrderByDirection;
}

export interface NullQueryObject {
  readonly limit$: undefined;
  readonly params$: undefined;
  readonly order$: undefined;
}

export interface QueryObject<K extends keyof Collections> {
  readonly limit$: Rx.Observable<number | undefined>;
  readonly params$: Rx.Observable<QueryParams<Collections[K]>>;
  readonly order$: Rx.Observable<Ordering<K> | undefined>;
}

export class NullQuery {
  get query(): NullQueryObject {
    return {
      limit$: undefined,
      params$: undefined,
      order$: undefined,
    };
  }
}

export class Query<K extends keyof Collections> {
  private constructor(
    private readonly params$:
      | Rx.Observable<QueryParams<Collections[K]>>
      | undefined,
    private readonly limitValue$: Rx.Observable<number> | undefined,
    private readonly orderByValue$: Rx.Observable<Ordering<K>> | undefined,
  ) {}

  static emptyQuery<K extends keyof Collections>() {
    return new Query<K>(undefined, undefined, undefined);
  }

  /**
   * Adds criteria to refine the results of a datastore list query.
   * The `field` and `value` parameters must have matching types according
   * to the model file of the desired collection (see `<collection>.model.ts`).
   *
   * For example, if we want to query the 'projects' collection by title, our
   * where clause should look like:
   *
   * `.where('title', '==', 'someStringTitle')`
   * `.where('rating', '<=', 10)`, or
   * `.where('seoUrl', 'equalsIgnoreCase', seoUrl)`
   * `.where('completeStatus', 'in', [BidCompleteStatusApi.COMPLETE, BidCompleteStatusApi.PENDING])`,
   * `.where('members', 'includes', userId$)`,
   * `.where('location', 'nearby', { range: 150_000, latitude, longitude })`, or
   * `.where('jobs', 'intersects', jobs)`.
   */
  where<T extends keyof Collections[K]>(
    field: T,
    condition: WhereFilterEqualsOp,
    value$: Collections[K][T] | Rx.Observable<Collections[K][T]>,
  ): Query<K>;
  where<T extends keyof Collections[K]>(
    field: T,
    condition: WhereFilterRangeOp,
    value$:
      | Collections[K][T] & number
      | Rx.Observable<Collections[K][T] & number>,
  ): Query<K>;
  where<T extends keyof Collections[K]>(
    field: T,
    condition: WhereNearbyFilterOp,
    value$: NearbyQueryParam | Rx.Observable<NearbyQueryParam>,
  ): Query<K>;
  where<T extends keyof Collections[K]>(
    field: T,
    condition: WhereFilterStringComparisonOp,
    value$:
      | (Collections[K][T] & string)
      | Rx.Observable<Collections[K][T] & string>,
  ): Query<K>;
  where<T extends keyof Collections[K]>(
    field: T,
    condition: WhereFilterInListOp,
    values$:
      | ReadonlyArray<Collections[K][T]>
      | Rx.Observable<ReadonlyArray<Collections[K][T]>>,
  ): Query<K>;
  where<T extends keyof Collections[K]>(
    field: T,
    condition: WhereFilterListIncludesOp,
    value$:
      | ArrayElement<Collections[K][T]>
      | Rx.Observable<ArrayElement<Collections[K][T]>>,
  ): Query<K>;
  where<T extends keyof Collections[K]>(
    field: T,
    condition: WhereFilterListIntersectsOp,
    values$:
      | ReadonlyArray<ArrayElement<Collections[K][T]>>
      | Rx.Observable<ReadonlyArray<ArrayElement<Collections[K][T]>>>,
  ): Query<K>;
  where<T extends keyof Collections[K]>(
    field: T,
    condition: WhereFilterOp,
    value$: any,
  ): Query<K> {
    const newParams$: Rx.Observable<QueryParams<Collections[K]>> = toObservable(
      value$,
    ).pipe(
      condition === 'in'
        ? distinctUntilChanged(arrayIsShallowEqual)
        : distinctUntilChanged(),
      map(v => {
        // massive ternary is because otherwise type error
        const param: QueryParams<Collections[K]>[T] =
          condition === 'in'
            ? { name: field, condition, values: v }
            : condition === '=='
            ? { name: field, condition, value: v }
            : condition === '<='
            ? { name: field, condition, value: v }
            : condition === '<'
            ? { name: field, condition, value: v }
            : condition === '>='
            ? { name: field, condition, value: v }
            : condition === '>'
            ? { name: field, condition, value: v }
            : condition === 'includes'
            ? { name: field, condition, value: v }
            : condition === 'intersects'
            ? { name: field, condition, values: v }
            : condition === 'nearby'
            ? { name: field, condition, value: v }
            : { name: field, condition, value: v };

        return { [field]: param } as any;
      }),
    );

    if (this.params$ === undefined) {
      return new Query(newParams$, this.limitValue$, this.orderByValue$);
    }

    return new Query(
      Rx.combineLatest(this.params$, newParams$).pipe(
        map(([params, p]) => Object.assign({}, params, p)),
        distinctUntilChanged(),
      ),
      this.limitValue$,
      this.orderByValue$,
    );
  }

  orderBy(
    field: Ordering<K>['field'],
    direction: Ordering<K>['direction'],
  ): Query<K> {
    return new Query(
      this.params$,
      this.limitValue$,
      Rx.of({ field, direction }),
    );
  }

  limit(n$: number | Rx.Observable<number>): Query<K> {
    if (this.limitValue$) {
      throw new Error("You can't call `limit` twice.");
    }

    return new Query(
      this.params$,
      toObservable(n$).pipe(distinctUntilChanged()),
      this.orderByValue$,
    );
  }

  /**
   * An empty query which does not trigger a network request. To be used with
   * dynamic queries (queries wrapped in Observables).
   *
   * Note that this will always result in an empty list, and is not the same as
   * passing the default query (`query => query`) or no query (`undefined`).
   */
  null(): NullQuery {
    return new NullQuery();
  }

  // Grab the properties of the query as observables
  get query(): QueryObject<K> {
    return {
      limit$: this.limitValue$ || Rx.of(undefined),
      params$: this.params$ || Rx.of({}),
      order$: this.orderByValue$ || Rx.of(undefined),
    };
  }
}
